import { api, type Person, type ShareInfo } from '../sync/api';
import { thDateTime } from '../time';
import { $ } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';

/*
 * "แชร์": share one of my designs with the whole group and/or chosen people.
 * People it is shared with can view, export and copy it; they cannot change it.
 * Every refusal from the server is shown with its reason, and the list reloads so it matches the server.
 */
export function initShareSheet(opts: { onChanged: () => void }) {
  const title = $('shTitle'), groupBox = $('shGroup') as HTMLInputElement, search = $('shSearch') as HTMLInputElement;
  const list = $('shPeople'), msg = $('shMsg');
  let design: { id: string; name: string } | null = null;
  let info: ShareInfo | null = null;
  let people: Person[] = [];
  let busy = false;

  const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
  const say = (text: string, ok = false) => { msg.textContent = text; msg.hidden = !text; msg.classList.toggle('ok', ok); };

  async function load() {
    if (!design) return;
    list.textContent = '';
    list.append(Object.assign(document.createElement('li'), { className: 'empty', textContent: 'กำลังโหลด…' }));
    try {
      const [s, p] = await Promise.all([api.shares(design.id), api.people()]);
      info = s; people = p.people;
      paint();
    } catch (e) {
      list.textContent = '';
      say('โหลดรายชื่อการแชร์ไม่สำเร็จ: ' + errText(e));
    }
  }

  function row(p: { id: string; name: string; email: string }, sub: string, action: 'share' | 'unshare', disabled = false) {
    const li = document.createElement('li');
    const av = Object.assign(document.createElement('span'), { className: 'avatar', textContent: (p.name || p.email).charAt(0).toUpperCase() });
    const who = document.createElement('span'); who.className = 'uwho';
    who.append(Object.assign(document.createElement('b'), { textContent: p.name || p.email.split('@')[0] }),
      Object.assign(document.createElement('small'), { textContent: sub }));
    const b = document.createElement('button');
    b.className = action === 'share' ? 'btn main' : 'btn danger';
    b.textContent = action === 'share' ? 'แชร์' : 'เลิกแชร์';
    b.setAttribute('aria-label', `${b.textContent} ${p.email}`);
    b.disabled = busy || disabled;
    b.onclick = () => void change(action, p.id, p.email);
    li.append(av, who, b);
    return li;
  }

  function paint() {
    if (!info) return;
    if (!busy) groupBox.checked = !!info.group;   // while a change is on its way, keep what the person just tapped
    groupBox.disabled = busy;
    $('shGroupNote').textContent = info.group
      ? `แชร์ทั้งกลุ่มแล้ว เมื่อ ${thDateTime(new Date(info.group.sharedAt))} (รวมคนที่ได้รับอนุมัติทีหลัง)`
      : 'สมาชิกที่ได้รับอนุมัติทุกคน รวมถึงคนที่เข้ามาทีหลัง';
    list.textContent = '';
    const shared = new Set(info.people.map(p => p.id));
    for (const p of info.people) {
      const status = p.status === 'disabled' ? ' · ปิดการใช้งานแล้ว' : '';
      list.append(row(p, `${p.email} · แชร์เมื่อ ${thDateTime(new Date(p.sharedAt))}${status}`, 'unshare'));
    }
    const q = search.value.trim().toLowerCase();
    const others = people.filter(p => !shared.has(p.id) && (!q || p.name.toLowerCase().includes(q) || p.email.includes(q)));
    others.forEach(p => list.append(row(p, p.email, 'share')));
    if (!info.people.length && !others.length) {
      list.append(Object.assign(document.createElement('li'), { className: 'empty', textContent: q ? 'ไม่พบสมาชิกที่ตรงกับคำค้นหา' : 'ยังไม่มีสมาชิกคนอื่นที่ได้รับอนุมัติ' }));
    }
  }

  async function change(action: 'share' | 'unshare', to: string, label: string) {
    if (!design || busy) return;
    busy = true; say(''); paint();
    try {
      if (action === 'share') {
        const r = await api.share(design.id, to);
        say(r.already ? `${label} ได้รับแชร์อยู่แล้ว` : `แชร์ให้ ${label} แล้ว`, true);
      } else {
        await api.unshare(design.id, to);
        say(`ยกเลิกการแชร์ให้ ${label} แล้ว`, true);
      }
      opts.onChanged();
    } catch (e) {
      say(`${action === 'share' ? 'แชร์' : 'ยกเลิกการแชร์'}ไม่สำเร็จ: ${errText(e)}`);
      toast(errText(e), true);
    } finally {
      busy = false;
      await load();
    }
  }

  groupBox.onchange = () => void change(groupBox.checked ? 'share' : 'unshare', 'group', 'ทุกคนในกลุ่ม');
  search.oninput = paint;

  return {
    open(d: { id: string; name: string }) {
      design = d; info = null; search.value = ''; say('');
      title.textContent = `แชร์ "${d.name}"`;
      openSheet('shShare');
      void load();
    }
  };
}
